import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ThemeConfig, ThemeContextValue } from '../types/theme';
import { availableThemes, getThemeById, getDefaultThemeId } from '../config/themes';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = 'hop-studio-theme';

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeConfig>(() => {
    // Try to load theme from localStorage first
    const savedThemeId = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedThemeId) {
      return getThemeById(savedThemeId);
    }
    // Fall back to default theme from environment or 'default'
    return getThemeById(getDefaultThemeId());
  });

  // Apply CSS custom properties when theme changes
  const applyCSSVariables = useCallback((theme: ThemeConfig) => {
    const root = document.documentElement;
    const { colors, typography, layout } = theme;

    // Color variables
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-primary-hover', colors.primaryHover);
    root.style.setProperty('--color-primary-active', colors.primaryActive);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-secondary-hover', colors.secondaryHover);
    root.style.setProperty('--color-secondary-active', colors.secondaryActive);
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-warning', colors.warning);
    root.style.setProperty('--color-error', colors.error);
    root.style.setProperty('--color-info', colors.info);

    // Background colors
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-background-secondary', colors.backgroundSecondary);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-surface-secondary', colors.surfaceSecondary);

    // Text colors
    root.style.setProperty('--color-text-primary', colors.textPrimary);
    root.style.setProperty('--color-text-secondary', colors.textSecondary);
    root.style.setProperty('--color-text-disabled', colors.textDisabled);
    root.style.setProperty('--color-text-inverse', colors.textInverse);

    // Border colors
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-border-secondary', colors.borderSecondary);
    root.style.setProperty('--color-border-hover', colors.borderHover);

    // Sidebar colors
    root.style.setProperty('--color-sidebar-background', colors.sidebarBackground);
    root.style.setProperty('--color-sidebar-text', colors.sidebarText);
    root.style.setProperty('--color-sidebar-text-hover', colors.sidebarTextHover);
    root.style.setProperty('--color-sidebar-text-active', colors.sidebarTextActive);

    // Header colors
    root.style.setProperty('--color-header-background', colors.headerBackground);
    root.style.setProperty('--color-header-text', colors.headerText);
    root.style.setProperty('--color-header-border', colors.headerBorder);

    // Typography
    root.style.setProperty('--font-family-base', typography.fontFamily);
    root.style.setProperty('--font-family-heading', typography.headingFontFamily);
    root.style.setProperty('--font-family-mono', typography.monospaceFontFamily);
    root.style.setProperty('--font-size-base', typography.fontSize.base);
    root.style.setProperty('--line-height-base', typography.lineHeight.normal.toString());

    // Layout
    root.style.setProperty('--sidebar-width', `${layout.sidebarWidth}px`);
    root.style.setProperty('--header-height', `${layout.headerHeight}px`);
    root.style.setProperty('--border-radius-base', layout.borderRadius.base);
    root.style.setProperty('--spacing-base', layout.spacing.base);

    // Shadows
    root.style.setProperty('--shadow-sm', layout.shadows.sm);
    root.style.setProperty('--shadow-base', layout.shadows.base);
    root.style.setProperty('--shadow-lg', layout.shadows.lg);
    root.style.setProperty('--shadow-xl', layout.shadows.xl);

    // Logo filter is no longer needed as dark mode is handled via algorithm
    // Dark mode body class and color-scheme are now managed by DarkModeContext
  }, []);

  // Update favicon when theme changes
  const updateFavicon = useCallback((theme: ThemeConfig) => {
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon && theme.branding.faviconUrl) {
      favicon.href = theme.branding.faviconUrl;
    }
  }, []);

  // Update document title when theme changes
  const updateDocumentTitle = useCallback((theme: ThemeConfig) => {
    document.title = theme.branding.appName;
  }, []);

  // Set theme function
  const setTheme = useCallback((themeId: string) => {
    const newTheme = getThemeById(themeId);
    setCurrentTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, themeId);
  }, []);

  // Customize theme function (for runtime theme modifications)
  const customizeTheme = useCallback((updates: Partial<ThemeConfig>) => {
    setCurrentTheme(prevTheme => ({
      ...prevTheme,
      ...updates,
    }));
  }, []);

  // Reset theme to default
  const resetTheme = useCallback(() => {
    const defaultThemeId = getDefaultThemeId();
    setTheme(defaultThemeId);
  }, [setTheme]);

  // Apply theme effects
  useEffect(() => {
    applyCSSVariables(currentTheme);
    updateFavicon(currentTheme);
    updateDocumentTitle(currentTheme);
  }, [currentTheme, applyCSSVariables, updateFavicon, updateDocumentTitle]);


  const contextValue: ThemeContextValue = {
    currentTheme,
    availableThemes,
    setTheme,
    customizeTheme,
    resetTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
